import React, { useState, useEffect } from 'react'
import { useNotificationContext } from '../../contexts/NotificationContext'
import { useUser } from '../../hooks/useUser'
import MapSelector from './MapSelector'
import InfrastructureDetailsModal from './InfrastructureDetailsModal'

interface InfrastructureObject {
  id: number
  client_id: number
  client_name: string
  name: string
  infrastructure_type: string
  address: string
  description: string
  location: string
  street_address: string
  latitude: string
  longitude: string
  created_at: string
}

interface InfrastructureListProps {
  token: string
}

export default function InfrastructureList({ token }: InfrastructureListProps) {
  const [infrastructure, setInfrastructure] = useState<InfrastructureObject[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingObject, setEditingObject] = useState<InfrastructureObject | null>(null)
  const [clients, setClients] = useState<{id: number, name: string}[]>([])
  const [selectedObject, setSelectedObject] = useState<InfrastructureObject | null>(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const { showSuccess, showError, showConfirm } = useNotificationContext()
  const { canManageUsers } = useUser(token)

  // Form state
  const [formData, setFormData] = useState({
    client_id: 0,
    name: '',
    infrastructure_type: '',
    address: '',
    description: '',
    location: '',
    street_address: '',
    latitude: '',
    longitude: ''
  })

  useEffect(() => {
    loadClients()
    loadInfrastructure()
  }, [])

  useEffect(() => {
    if (searchTerm) {
      searchInfrastructure()
    } else {
      loadInfrastructure()
    }
  }, [searchTerm])



  const loadClients = async () => {
    try {
      const response = await fetch('/api/clients/list', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setClients(data)
      }
    } catch {
      // Ignore client loading errors for now
    }
  }

  const loadInfrastructure = async () => {
    try {
      setLoading(true)
      const response = await fetch('/api/infrastructure/', {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.ok) {
        const data = await response.json()
        setInfrastructure(data)
      } else {
        setError('Błąd podczas ładowania obiektów infrastruktury')
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem')
    } finally {
      setLoading(false)
    }
  }

  const searchInfrastructure = async () => {
    try {
      setLoading(true)
      const response = await fetch(`/api/infrastructure/?search=${encodeURIComponent(searchTerm)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.ok) {
        const data = await response.json()
        setInfrastructure(data)
      } else {
        setError('Błąd podczas wyszukiwania obiektów infrastruktury')
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      setError('Nazwa obiektu jest wymagana')
      return
    }

    if (!formData.client_id) {
      setError('Wybierz klienta')
      return
    }

    try {
      const url = editingObject ? `/api/infrastructure/${editingObject.id}` : '/api/infrastructure/'
      const method = editingObject ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      if (response.ok) {
        const action = editingObject ? 'zaktualizowany' : 'utworzony'
        showSuccess(`Obiekt infrastruktury został ${action} pomyślnie`)
        resetForm()
        loadInfrastructure()
      } else {
        const errorData = await response.json()
        showError('Błąd operacji', errorData.detail || `Błąd podczas ${editingObject ? 'aktualizacji' : 'tworzenia'} obiektu infrastruktury`)
      }
    } catch (err) {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    }
  }

  const handleEdit = (obj: InfrastructureObject) => {
    setEditingObject(obj)
    setFormData({
      client_id: obj.client_id,
      name: obj.name,
      infrastructure_type: obj.infrastructure_type,
      address: obj.address,
      description: obj.description,
      location: obj.location,
      street_address: obj.street_address,
      latitude: obj.latitude,
      longitude: obj.longitude
    })
    setShowForm(true)
  }

  const handleDelete = async (objectId: number, objectName: string) => {
    const confirmed = await showConfirm(
      'Potwierdzenie usunięcia',
      `Czy na pewno chcesz usunąć obiekt infrastruktury "${objectName}"?`
    )

    if (!confirmed) {
      return
    }

    try {
      const response = await fetch(`/api/infrastructure/${objectId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.ok) {
        showSuccess('Obiekt infrastruktury został usunięty pomyślnie')
        loadInfrastructure()
      } else {
        const errorData = await response.json()
        showError('Błąd usuwania', errorData.detail || 'Błąd podczas usuwania obiektu infrastruktury')
      }
    } catch (err) {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    }
  }

  const resetForm = () => {
    setFormData({
      client_id: 0,
      name: '',
      infrastructure_type: '',
      address: '',
      description: '',
      location: '',
      street_address: '',
      latitude: '',
      longitude: ''
    })
    setEditingObject(null)
    setShowForm(false)
    setError('')
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  if (loading && infrastructure.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Ładowanie obiektów infrastruktury...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Obiekty Infrastruktury</h2>
        {canManageUsers() && (
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
          >
            + Nowy Obiekt
          </button>
        )}
      </div>

      {/* Search */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Wyszukaj obiekt
          </label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Wprowadź nazwę obiektu..."
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Infrastructure form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingObject ? 'Edytuj Obiekt Infrastruktury' : 'Nowy Obiekt Infrastruktury'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Klient *
                  </label>
                  <select
                    name="client_id"
                    value={formData.client_id}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">— wybierz klienta —</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nazwa obiektu *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Wprowadź nazwę obiektu"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rodzaj obiektu
                  </label>
                  <select
                    name="infrastructure_type"
                    value={formData.infrastructure_type}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">— wybierz rodzaj —</option>
                    <option value="Stacja Uzdatniania wody">Stacja Uzdatniania wody</option>
                    <option value="Hydroforownia">Hydroforownia</option>
                    <option value="Przepompownia ścieków">Przepompownia ścieków</option>
                    <option value="Oczyszczalnia ścieków">Oczyszczalnia ścieków</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adres
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Wprowadź adres"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Miejscowość
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="np. Warszawa"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ulica/nr działki
                  </label>
                  <input
                    type="text"
                    name="street_address"
                    value={formData.street_address}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="np. ul. Przykładowa 123"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Szerokość geo.
                    </label>
                    <input
                      type="text"
                      name="latitude"
                      value={formData.latitude}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="np. 52.2297"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Długość geo.
                    </label>
                    <input
                      type="text"
                      name="longitude"
                      value={formData.longitude}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="np. 21.0122"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lokalizacja na mapie
                  </label>
                  <div className="mb-2">
                    <MapSelector
                      latitude={formData.latitude}
                      longitude={formData.longitude}
                      onLocationChange={(lat, lng) => {
                        setFormData(prev => ({
                          ...prev,
                          latitude: lat,
                          longitude: lng
                        }))
                      }}
                      height="300px"
                    />
                  </div>
                  <div className="text-xs text-gray-500">
                    Kliknij na mapie aby wybrać lokalizację lub przeciągnij istniejący znacznik
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Opis
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Opcjonalny opis obiektu"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-medium"
                  >
                    Anuluj
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
                  >
                    {editingObject ? 'Zaktualizuj' : 'Utwórz'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Infrastructure table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nazwa
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Klient
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Lokalizacja
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Adres
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Akcje
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {infrastructure.map((obj) => (
              <tr key={obj.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => {
                      setSelectedObject(obj)
                      setShowDetailsModal(true)
                    }}
                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                  >
                    {obj.name}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {obj.client_name}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {obj.location || '-'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  <div className="max-w-xs truncate" title={obj.street_address}>
                    {obj.street_address || '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                  {canManageUsers() ? (
                    <>
                      <button
                        onClick={() => handleEdit(obj)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edytuj
                      </button>
                      <button
                        onClick={() => handleDelete(obj.id, obj.name)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Usuń
                      </button>
                    </>
                  ) : (
                    <span className="text-gray-400">Tylko do odczytu</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {infrastructure.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-500">
            {searchTerm ? 'Brak obiektów spełniających kryteria wyszukiwania' : 'Brak obiektów infrastruktury w systemie'}
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="text-sm text-blue-800">
          Liczba obiektów infrastruktury: <strong>{infrastructure.length}</strong>
          {searchTerm && (
            <span className="ml-4">
              Wyszukiwanie: "<strong>{searchTerm}</strong>"
            </span>
          )}
        </div>
      </div>

      {/* Infrastructure Details Modal */}
      <InfrastructureDetailsModal
        infrastructureObject={selectedObject}
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false)
          setSelectedObject(null)
        }}
        token={token}
      />
    </div>
  )
}
